import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    IconButton,
    Menu,
    MenuItem,
    FormControl,
    Chip,
    Card,
    CardContent,
} from '@mui/material';
import { styled } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import AppTextField from './CustomTextField';
import { TableActionItem, TableColumn, TableFetchParams, TableTabItem } from '@/types';
import moment from 'moment';
import _ from 'lodash';
import TableSkeletonRows from './TableSkeletonRows';

// Styled component for tabs
const StyledTab = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    color: '#121C2D',
    backgroundColor: '#fff',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
    minHeight: 34,
    padding: '4px 16px',
    border: '1px solid #8891AA',
    borderRadius: '100px',
    cursor: 'pointer',
    transition: '500ms',
    marginTop: 3,
    marginBottom: 3,
    '&:not(:last-child)': {
        marginRight: 6,
    },
    '&:hover': {
        color: '#030B5D',
        borderColor: '#030B5D',
        backgroundColor: '#CCE4FF',
    },
    '&.selected': {
        color: '#030B5D',
        borderColor: '#030B5D',
        backgroundColor: '#CCE4FF',
    },
});

// Styled Table Cell & Row
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    background: '#F6F6F6',
}));

// --- Form Card Styling --- //
const FormCard = styled(Box)(({ theme }) => ({
    backgroundColor: '#fff',
    border: '1px solid #eee',
    padding: theme.spacing(3, 2.25),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2.5),
    '&.gray-border': {
        borderColor: '#C2C2C2',
    },
    '&.blue-border': {
        borderColor: '#ACB6F4',
    },
    '&.shadow': {
        border: 'none',
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.15) !important',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-root': {
        background: theme.palette.primary.main,
        color: '#fff !important',
        '& .MuiCheckbox-root': {
            '& .MuiSvgIcon-root': {
                fill: '#ccc',
            },
            '&.Mui-checked': {
                '& .MuiSvgIcon-root': {
                    fill: '#fff',
                },
            },
        },
    },
}));

// Props interface; fetchData callback now expects TableFetchParams
interface CustomTableProps {
    columns: TableColumn[];
    actions?: TableActionItem[] | ((row: any) => TableActionItem[]);
    tableTabData?: TableTabItem[];
    onTabClick?: (item: TableTabItem, index: number) => void;
    fetchData: (params: TableFetchParams) => Promise<{ data: any[]; total: number }>;
    hideDownload?: boolean
    hideSearch?: boolean
}

export interface TenantTableRef {
    refetch: () => void;
}

const TenantTable = forwardRef<TenantTableRef, CustomTableProps>(({
    columns,
    actions,
    tableTabData,
    onTabClick,
    fetchData,
    hideDownload = false,
    hideSearch = false
}, ref) => {

    // Use URL query params so table state persists in the URL (like CustomTable).
    const [searchParams, setSearchParams] = useSearchParams();

    // Build params object from URL (0-indexed page stored in URL)
    const params = useMemo<TableFetchParams>(() => {
        const getParam = <T extends string>(key: string, fallback: T): T =>
            (searchParams.get(key) as T) || fallback;

        return {
            search: getParam('search', ''),
            page: parseInt(getParam('page', '0'), 10),
            limit: parseInt(getParam('limit', '10'), 10),
            sortField: getParam('sortField', 'id'),
            sortOrder: getParam('sortOrder', 'DESC'),
            filters: JSON.parse(getParam('filters', '[]')),
        };
    }, [searchParams]);
    const { page, limit } = params;

    // New state for handling live search input for debouncing (mirrors URL).
    const [searchTerm, setSearchTerm] = useState<string>(params.search);

    const [data, setData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const requestSequenceRef = useRef(0);

    // Internal state for export & action menus.
    const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
    const [currentRow, setCurrentRow] = useState<any>(null);
    const actionMenuOpen = Boolean(actionAnchorEl);

    // Convenience fn to write new params to URL
    const updateParams = useCallback((newParams: Partial<TableFetchParams>) => {
        const updated = { ...params, ...newParams };
        const nextSearchParams = new URLSearchParams(searchParams);

        if (updated.search.trim()) {
            nextSearchParams.set('search', updated.search);
        } else {
            nextSearchParams.delete('search');
        }
        nextSearchParams.set('page', String(updated.page));
        nextSearchParams.set('limit', String(updated.limit));
        nextSearchParams.set('sortField', updated.sortField ?? 'id');
        nextSearchParams.set('sortOrder', updated.sortOrder ?? 'DESC');
        nextSearchParams.set('filters', JSON.stringify(updated.filters ?? []));
        setSearchParams(nextSearchParams);
    }, [params, searchParams, setSearchParams]);

    // Effect for debouncing the search input (500ms delay).
    useEffect(() => {
        const shouldRemoveEmptySearch = !searchTerm.trim() && searchParams.has('search');
        if (searchTerm === params.search && !shouldRemoveEmptySearch) return;

        const handler = setTimeout(() => {
            // Update the URL params (search + reset page) after 500ms of inactivity.
            updateParams({ search: searchTerm, page: 0 });
        }, 500);
        return () => clearTimeout(handler);
    }, [params.search, searchParams, searchTerm, updateParams]);

    // keep searchTerm in sync when URL changes (e.g. user shares link or uses back/forward)
    useEffect(() => {
        setSearchTerm(params.search);
    }, [params.search]);

    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    // Function to load data from API using fetchData callback.
    // Note: API might expect 1-indexed page so we pass page + 1.
    const loadData = useCallback(async () => {
        const requestSequence = ++requestSequenceRef.current;
        setIsLoading(true);

        try {
            const res = await fetchDataRef.current({
                ...params,
                page: params.page + 1, // Convert to 1-indexed if API
                skipGlobalLoader: true,
            });
            if (requestSequence === requestSequenceRef.current) {
                setData(res.data);
                setTotalCount(res.total);
            }
        } catch (error) {
            if (requestSequence === requestSequenceRef.current) {
                setData([]);
                setTotalCount(0);
                console.error('Error fetching table data:', error);
            }
        } finally {
            if (requestSequence === requestSequenceRef.current) {
                setIsLoading(false);
            }
        }
    }, [params]);

    useImperativeHandle(ref, () => ({
        refetch: loadData,
    }), [loadData]);

    // Call loadData whenever URL params change
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handler for live search input change.
    // This updates the searchTerm, which in turn is debounced.
    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        updateParams({ search: '', page: 0 });
    };

    // Handler for page change.
    const handleChangePage = (
        _: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        updateParams({ page: newPage });
    };

    // Handler for rows per page change.
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        updateParams({ limit: parseInt(event.target.value, 10), page: 0 });
    };

    // Handler for tab click which updates the filter value and resets page.
    const handleTabClick = (item: TableTabItem, index: number) => {
        const { key, value } = item;
        const filters = key && value ? [{ [key]: value }] : [];
        updateParams({ filters, page: 0 });
        if (onTabClick) onTabClick(item, index);
    };

    // Handler for sorting when table header is clicked.
    const handleSort = (col: TableColumn) => {
        if (col.sortable) {
            // Determine the field to sort on (default to accessor if sortField is not provided).
            const field = col.sortField || col.accessor;
            const nextOrder =
                params.sortField === field && params.sortOrder === 'ASC' ? 'DESC' : 'ASC';
            updateParams({ sortField: field, sortOrder: nextOrder });
        }
    };

    // Action menu handlers.
    const handleActionButtonClick = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
        setActionAnchorEl(event.currentTarget);
        setCurrentRow(row);
    };

    const handleActionMenuClose = () => {
        setActionAnchorEl(null);
        setCurrentRow(null);
    };

    return (
        <FormCard className="shadow">
            {/* Top Section with Tabs and Search */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', pb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                    {tableTabData?.map((item, idx) => (
                        <StyledTab
                            key={idx}
                            onClick={() => handleTabClick(item, idx)}
                            className={item.selected ? 'selected' : ''}
                        >
                            {`${item.name} (${item.count || 0})`}
                        </StyledTab>
                    ))}
                </Box>
                {!hideSearch && <Box sx={{ display: 'inline-flex', ml: 'auto' }}>
                    <FormControl sx={{ display: 'inline-flex', alignSelf: 'center', '&:not(:last-child)': { mr: 2 } }}>
                        <Box sx={{ display: 'inline-flex', position: 'relative', width: 276 }}>
                            <IconButton
                                type="submit"
                                aria-label="search"
                                sx={{
                                    display: 'inline-flex',
                                    position: 'absolute',
                                    top: 1,
                                    left: 2,
                                    zIndex: 1,
                                    width: 34,
                                    height: 34,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: 'transparent !important',
                                    borderRadius: '8px 0 0 8px',
                                }}
                            >
                                <SearchIcon fontSize="small" sx={{ color: '#606B85' }} />
                            </IconButton>
                            <AppTextField
                                onChange={(e) => handleSearchChange(e.target.value)}
                                label=""
                                value={searchTerm}
                                placeholder="Search"
                                InputProps={{
                                    endAdornment: searchTerm ? (
                                        <IconButton
                                            aria-label="Clear search"
                                            edge="end"
                                            size="small"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={handleClearSearch}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    ) : undefined,
                                }}
                                sx={{
                                    opacity: 1,
                                    '& .MuiInputBase-root': {
                                        marginTop: '0px !important',
                                        backgroundColor: '#fff !important',
                                        pl: 3.25,
                                        '& .MuiInputBase-input': {
                                            minHeight: '36px !important',
                                            py: '6px !important',
                                            fontSize: 14,
                                            backgroundColor: '#fff !important',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </FormControl>
                </Box>}
            </Box>

            <Card variant="outlined" elevation={0} sx={{ boxShadow: 'none', p: 0 }}>
                <CardContent sx={{ p: 0 }}>
                    {/* Table Content */}
                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead sx={{ backgroundColor: "#F6F6F6" }}>
                                <StyledTableRow>
                                    {columns.map((col, index) => (
                                        <StyledTableCell
                                            className="text-nowrap"
                                            key={index}
                                            onClick={() => handleSort(col)}
                                            style={{ cursor: col.sortable ? 'pointer' : 'default' }}
                                        >
                                            {col.header}
                                            {col.sortable && (
                                                <>
                                                    {params.sortField === (col.sortField || col.accessor) ? (
                                                        params.sortOrder === 'ASC' ? ' ▲' : ' ▼'
                                                    ) : (
                                                        ' ⇅'
                                                    )}
                                                </>
                                            )}
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell
                                        sx={{ position: 'sticky', right: -1, zIndex: 3 }}
                                    >
                                        {actions && !hideDownload && <IconButton type="submit" aria-label="download" size="medium">
                                            <img src="/images/download-icon.svg" alt="" />
                                        </IconButton>}
                                    </StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody aria-busy={isLoading}>
                                {isLoading ? (
                                    <TableSkeletonRows
                                        columnCount={columns.length}
                                        rowCount={Math.min(Math.max(limit, 5), 10)}
                                        includeActions={Boolean(actions)}
                                    />
                                ) : data.length > 0 ? (
                                    data.map((row, rowIndex) => (
                                        <TableRow key={rowIndex}
                                            className={row?.createdAt && moment().diff(moment(row?.createdAt), 'minutes') < 5 ? 'Mui-selected' : row?.ticketSlaHistoryData?.length > 0 ? 'Mui-selected-red' : ''}
                                        >
                                            {columns.map((col, colIndex) => (
                                                <StyledTableCell className="text-nowrap position-relative" key={colIndex}>
                                                    {col.render ? col.render(row) : _.get(row, col.accessor)}
                                                    {/* {col.render ? col.render(row) : row[col.accessor] } */}

                                                    {
                                                        colIndex === 0 &&
                                                        !!(row?.createdAt && moment().diff(moment(row?.createdAt), 'minutes') < 5) &&
                                                        <Chip size="small" label="New" color="success" className="newChip" />
                                                    }
                                                </StyledTableCell>
                                            ))}
                                            {actions && (
                                                <StyledTableCell sx={{ position: 'sticky', right: -1, zIndex: 1, backgroundColor: 'background.paper' }}>
                                                    <IconButton
                                                        onClick={(e) => handleActionButtonClick(e, row)}
                                                        aria-controls={actionMenuOpen ? 'action-menu' : undefined}
                                                        aria-haspopup="true"
                                                        aria-expanded={actionMenuOpen ? 'true' : undefined}
                                                    >
                                                        <img src="/images/three-dot.svg" alt="options" />
                                                    </IconButton>
                                                </StyledTableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <StyledTableCell
                                            colSpan={
                                                columns.length + (actions ? 1 : 0)
                                            }
                                            align="center"
                                            style={{ padding: '1.5rem' }}
                                        >
                                            <Box>No data available</Box>
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                        </Table>
                    </TableContainer>
                    {!isLoading && data.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={totalCount}
                            page={page}
                            rowsPerPage={limit}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            component="div"
                            sx={{ '& p': { marginBottom: 0 } }}
                        />
                    )}
                </CardContent>
            </Card>
            {/* Action Menu */}
            {actions && (
                <Menu
                    anchorEl={actionAnchorEl}
                    open={actionMenuOpen}
                    onClose={handleActionMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {(typeof actions === 'function' ? actions(currentRow) : actions || []).map((action, idx) => (
                        <MenuItem
                            key={idx}
                            onClick={() => {
                                action.handleAction(currentRow);
                                handleActionMenuClose();
                            }}
                        >
                            {action.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </FormCard>
    );
});

export default TenantTable
